import { cloneDeep } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material";
import useDebounce from "app/hooks/useDebounce";
import { initialPatientTask } from "app/store/transmission/slice/state/transmissionPatientModalState";
import {
  toggleModal,
  setPatientCurrentTask,
  setFetchPatientTasks,
} from "app/store/transmission/slice/transmissionSlice";
import { useSavePatientTaskMutation } from "app/store/transmission/api/transmissionTableApi";
import { showToast } from "utils/toast";
import { getCurrentUserRole } from "utils/currentUser.util";
import { patientProfileTaskApi } from "app/store/patientProfile/tasks/api/patientProfileTaskApi";
import { patientApi } from "app/store/patient/api/patientApi";

const usePatientTasks = () => {
  const theme = useTheme();
  const {
    patientId,
    isPatientTaskModal,
    patientTaskAction,
    currentPatientTask,
    fetchPatientTasks,
    originalPatientTask,
    originalProfile,
    profile
  } = useSelector((state) => state.transmission);
  const {currentUser} = useSelector(state =>state.auth)
  const dispatch = useDispatch();

  const currentUserRole = getCurrentUserRole();

  const [patientTask, setPatientTask] = useState(initialPatientTask);

  useEffect(() => {
    if (currentPatientTask) {
      const copy = cloneDeep({
        ...currentPatientTask,
        notes: currentPatientTask?.note?.note ?? currentPatientTask?.notes,
        ...(patientTaskAction === "Add" && {
          account: originalProfile?.account || "",
          status: "To Do",
        }),
      });
      setPatientTask({ ...copy });
    }
  }, [currentPatientTask, originalProfile?.account, patientTaskAction]);

  const debounceDispatch = useCallback(
    useDebounce((value) => {
      dispatch(setPatientCurrentTask(value));
    }, 500),
    []
  );

  const handleInputChange = async (value, name) => {
    const copy = cloneDeep(patientTask);
    copy[name] = value;
    setPatientTask({ ...copy });
    debounceDispatch(copy);
  };

  const onClose = () => {
    dispatch(
      toggleModal({
        type: "patientTask",
        value: { open: false, action: "", task: {} },
      })
    );
  };

  const [savePatientTask, { isLoading: isSubmitting }] =
    useSavePatientTaskMutation();

  const handleSubmit = async (values) => {
    try {
      const payload = {
        description: patientTask.description,
        status: patientTask.status,
        account: patientTask.account,
        patientName: originalProfile?.name,
        patientSerialNumber: originalProfile?.serial_number,
        notes: Array.isArray(values?.notes) ? values.notes : patientTask?.notes,
      };

      if (patientTaskAction === "Edit") {
        payload._id = patientTask._id;
      }
      const result = await savePatientTask({
        patientId,
        task: payload,
        isEditTask: patientTaskAction === "Edit",
      }).unwrap();
      if (!result?.error && result?.message) {
        dispatch(patientProfileTaskApi.util.resetApiState());
        if (payload?.notes) {
          dispatch(patientApi.util.invalidateTags(["patientNotesHistory"]));
        }
        showToast({ message: result?.message });
        onClose();
        await dispatch(setFetchPatientTasks(true));
      }
    } catch (err) {
      showToast({ message: err?.message, type: "error" });
    } finally {
      await dispatch(setFetchPatientTasks(false));
    }
  };

  return {
    theme,
    dispatch,
    onClose,
    patientId,
    isSubmitting,
    patientTask,
    currentUserRole,
    fetchPatientTasks,
    isPatientTaskModal,
    patientTaskAction,
    originalPatientTask,
    currentPatientTask,
    handleInputChange,
    handleSubmit,
    profile,
    currentUser
  };
};

export default usePatientTasks;
